/**
 * Güne göre ayrılmış çevrimiçi süre takibi.
 *
 * `Employees/{id}.totalOnlineMs` ömür boyu tek bir sayaçtır: "bu çalışan
 * toplamda ne kadar sistemde kaldı" sorusunu yanıtlar ama "bu ay ne kadar
 * kaldı" sorusunu yanıtlayamaz — tarih aralığına göre dilimlenemez.
 *
 * Ekip yöneticilerinin (SMO Lead) rapor alabilmesi için aynı nabız akışı süreyi
 * bir de güne bölünmüş olarak yazar:
 *
 *   Employees/{id}/dailyActivity/{YYYY-MM-DD}
 *     date      'YYYY-MM-DD'  — doküman id'si ile aynı, sorgulanabilsin diye alan olarak da tutulur
 *     onlineMs  o gün birikmiş çevrimiçi milisaniye
 *     sessions  o gün başlayan sekme oturumu sayısı
 *     lastSeen  o güne ait son yazma anı
 *
 * Maliyet: TopBar'daki 5 dakikalık flush başına bir ek yazı (gün sınırını aşan
 * flush'larda iki). Günler yerel saate göre ayrılır; raporu okuyan da aynı
 * saat diliminde olduğu için gün sınırları kullanıcının gördüğüyle uyuşur.
 */

import { collection, doc, getDocs, increment, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';

export const DAY_MS = 24 * 60 * 60 * 1000;

export const DAILY_ACTIVITY_COLLECTION = 'dailyActivity';

/**
 * Tek bir flush'ta eklenebilecek en uzun süre.
 *
 * Bilgisayar uyku moduna girdiğinde zamanlayıcı durur; uyanışta iki flush
 * arasında saatler görünebilir. Bu süre gerçekte sistemde geçirilmiş değildir,
 * bu yüzden flush aralığının iki katıyla kırpılır.
 */
export const MAX_FLUSH_MS = 10 * 60 * 1000;

/** Yerel saate göre 'YYYY-MM-DD'. */
export const dayKeyOf = (value = Date.now()) => {
  const d = value instanceof Date ? value : new Date(value);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Verilen andan itibaren yerel günün bitişi (ertesi günün 00:00'ı). */
const nextMidnight = (ms) => {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime();
};

/**
 * [fromMs, toMs) aralığını gün sınırlarından böler.
 * Gece yarısını aşan bir oturum iki güne doğru şekilde dağıtılır.
 */
export const splitByDay = (fromMs, toMs) => {
  const segments = [];
  let cursor = fromMs;
  while (cursor < toMs) {
    const segmentEnd = Math.min(nextMidnight(cursor), toMs);
    segments.push({ day: dayKeyOf(cursor), ms: segmentEnd - cursor });
    cursor = segmentEnd;
  }
  return segments;
};

/** Geçen süreyi ilgili gün(ler)in sayacına ekler. Hata yutulur — takip kritik değil. */
export const accumulateDailyActivity = (employeeId, fromMs, toMs) => {
  if (!employeeId || !(toMs > fromMs)) return Promise.resolve();
  return Promise.all(
    splitByDay(fromMs, toMs).map(({ day, ms }) =>
      setDoc(
        doc(db, 'Employees', employeeId, DAILY_ACTIVITY_COLLECTION, day),
        { date: day, onlineMs: increment(ms), lastSeen: serverTimestamp() },
        { merge: true }
      )
    )
  ).catch((error) => {
    console.debug('Failed to record daily activity:', error);
  });
};

/** Yeni bir sekme oturumu açıldığında o günün oturum sayacını artırır. */
export const markDailySession = (employeeId, when = Date.now()) => {
  if (!employeeId) return Promise.resolve();
  const day = dayKeyOf(when);
  return setDoc(
    doc(db, 'Employees', employeeId, DAILY_ACTIVITY_COLLECTION, day),
    { date: day, sessions: increment(1), lastSeen: serverTimestamp() },
    { merge: true }
  ).catch((error) => {
    console.debug('Failed to record daily session:', error);
  });
};

/** Bir çalışanın `sinceDayKey` (dahil) sonrası günlük kayıtları. */
export const fetchEmployeeDailyActivity = async (employeeId, sinceDayKey) => {
  const activityRef = collection(db, 'Employees', employeeId, DAILY_ACTIVITY_COLLECTION);
  const snapshot = await getDocs(query(activityRef, where('date', '>=', sinceDayKey)));
  return snapshot.docs.map((d) => {
    const data = d.data();
    return {
      date: data.date || d.id,
      onlineMs: typeof data.onlineMs === 'number' ? data.onlineMs : 0,
      sessions: typeof data.sessions === 'number' ? data.sessions : 0,
    };
  });
};

/** 5400000 → "1 sa 30 dk" */
export const formatDuration = (ms) => {
  if (!ms || ms <= 0) return '—';
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours} sa ${minutes} dk`;
  if (minutes > 0) return `${minutes} dk`;
  return `${Math.max(1, Math.floor(ms / 1000))} sn`;
};

/** Grafik ekseni için ondalıklı saat. */
export const toHours = (ms) => Math.round(((ms || 0) / 3600000) * 10) / 10;

/** "3 saat önce" */
export const formatRelative = (ms) => {
  if (!ms) return 'Aktivite yok';
  const diff = Date.now() - ms;
  if (diff < 60000) return 'Az önce';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} dk önce`;
  if (diff < DAY_MS) return `${Math.floor(diff / 3600000)} saat önce`;
  return `${Math.floor(diff / DAY_MS)} gün önce`;
};

/** "12.08.2026 14:30" */
export const formatDateTime = (ms) =>
  ms
    ? new Date(ms).toLocaleString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

/** "12.08" — grafik etiketleri için kısa gün. */
export const formatDayLabel = (dayKey) => {
  const [, month, day] = (dayKey || '').split('-');
  return month && day ? `${day}.${month}` : dayKey;
};
